export interface PricingCheckControlState {
  visible: boolean;
  disabled: boolean;
  running: boolean;
  /** The full action or reason: the tooltip and, when disabled, the accessible name. */
  label: string;
}

/** The pricing-check button's visible text title (HS2-CNXFTE). */
export const PRICING_CHECK_SHORT_LABEL = 'Price Check';

/** Derive the main-toolbar pricing-check control across every demand state
 *  (HS2-PMPDD9). Kept pure so the disabled/running transition matrix is pinned
 *  independently of the shell renderer. A check can run from the first line item:
 *  lines without a quote are benchmarked to an expected price range (HS2-CNXFTE). */
export function pricingCheckControlState(input: {
  onDemand: boolean;
  requester: boolean;
  readOnly: boolean;
  hasLines: boolean;
  running: boolean;
  hasAnalysis: boolean;
}): PricingCheckControlState {
  if (!input.onDemand || input.requester)
    return { visible: false, disabled: true, running: false, label: 'Run Pricing Check' };
  if (input.running) return { visible: true, disabled: true, running: true, label: 'Running pricing check' };
  if (input.readOnly)
    return { visible: true, disabled: true, running: false, label: 'Reopen Demand to Run Pricing Check' };
  if (!input.hasLines)
    return { visible: true, disabled: true, running: false, label: 'Add a Line Item to Run Pricing Check' };
  return {
    visible: true,
    disabled: false,
    running: false,
    label: input.hasAnalysis ? 'Re-Run Pricing Check' : 'Run Pricing Check',
  };
}

/** The prefs key remembering that this viewer dismissed one demand's prompt. */
export function pricingPromptPrefKey(demandId: string): string {
  return `pricing-check-prompt-dismissed:${demandId}`;
}

/**
 * Whether the demand editor shows the "run a pricing check" prompt banner
 * (HS2-CNXFTE): a staff member can run the check now, none has been run on this
 * demand yet, and they have not dismissed the prompt for it.
 */
export function pricingPromptVisible(input: {
  control: PricingCheckControlState;
  hasAnalysis: boolean;
  dismissed: boolean;
}): boolean {
  return input.control.visible && !input.control.disabled && !input.hasAnalysis && !input.dismissed;
}
